import type { Breakpoints } from "$src/core/models/theme";

const BREAKPOINT_ORDER: (keyof Breakpoints)[] = [
  "xs",
  "sm",
  "md",
  "lg",
  "xl",
  "xxl",
  "xxxl",
];

const breakpointOrder = (key: keyof Breakpoints): number =>
  BREAKPOINT_ORDER.indexOf(key);

/**
 * Find the breakpoint that is closest to the current breakpoint that has a value
 * defined, in a mobile-first / min-width approach, which mirrors Unistyles behavior.
 */
const pickClosestResponsiveBreakpoint = (
  breakpoints: Partial<Breakpoints>,
  breakpoint: keyof Breakpoints,
): keyof Breakpoints | undefined => {
  const allBreakpoints = Object.entries(breakpoints).sort(
    (a, b) => a[1] - b[1],
  );
  if (allBreakpoints.length === 0) return undefined;

  const index = allBreakpoints.findIndex(([key]) => key === breakpoint);
  if (index !== -1) {
    const allBreakpointsAfterCurrent = allBreakpoints
      .slice(index)
      .map(([key]) => key);
    return allBreakpointsAfterCurrent[0] as keyof Breakpoints;
  }

  const currentOrder = breakpointOrder(breakpoint);
  const firstKey = allBreakpoints[0][0] as keyof Breakpoints;
  const lastKey = allBreakpoints[
    allBreakpoints.length - 1
  ][0] as keyof Breakpoints;
  if (currentOrder < breakpointOrder(firstKey)) return undefined;
  if (currentOrder > breakpointOrder(lastKey)) return lastKey;
  const firstAtOrAfter = allBreakpoints.find(
    ([key]) => breakpointOrder(key as keyof Breakpoints) >= currentOrder,
  );
  return firstAtOrAfter?.[0] as keyof Breakpoints | undefined;
};

export default pickClosestResponsiveBreakpoint;
